import { Component, Host, h, Prop } from '@stencil/core';
import { default as languageStrings } from '../../global/l10n/NotifyMe.json';

@Component({
    tag: 'ti-notify-me',
    styleUrl: 'notify-me.scss',
    shadow: true,
})
export class NotifyMe {
    /**
     * Dialog launching service
     * @private
     */
    private _dialogService;

    /**
     * Locale-specific set of language strings
     * @private
     * @type {Record<string, string>}
     */
    private _localeStrings: Record<string, string>

    /**
    * URL for API call to create the notification request
    * Note that, if the default is being used, '{siteId}' will be replaced with the provided siteId prop, which itself defaults to 'ti'
    * @type {string}
    * @default '/alerts/{siteId}/backinstock/notificationrequest'
    */
    @Prop({ mutable: true, reflect: true }) endpointUrl: string = '/alerts/{siteId}/backinstock/notificationrequest';

    /**
     * The GPN of the part that needs the notification
     * @type {string}
     */
    @Prop({ mutable: true }) gpn: string;

    /**
     * Locale, defaults to en-US
     * @type {string}
     * @default 'en-US'
     */
    @Prop({ mutable: true }) locale: string = 'en-US';

    /**
     * The OPN of the part that needs the notification
     * @type {string}
     */
    @Prop({ mutable: true }) opn!: string;

    /**
     * The store, for the API call
     * Only used if user is using the default endpointUrl
     * @default 'ti'
     */
    @Prop({ mutable: true }) siteId: string = 'ti';

    /**
    * Loads the labels and replaces the "{siteId}" in the endpointUrl
    */
    async componentWillLoad() {
        this._localeStrings = languageStrings[this.locale] || languageStrings['en-US'] || {};
        if (this.endpointUrl.includes('{siteId}')) {
            this.endpointUrl = this.endpointUrl.replace('{siteId}', this.siteId);
        }
    }

    /**
     * Closes the dialog
     */
    private _closeDialog() {
        this._dialogService.closeDialog();
    }

    /**
     * Fetches the content for the dialog
     */
    private _fetchDialogContent() {
        const dialog = ([
            <h2 slot="title">{this._localeStrings['notifyMeLabel']}</h2>,
            <div class="ti-notify-me-body">
                <p>{this._localeStrings['instructionsText']}</p>
            </div>,
            <ti-button slot="action" appearance="primary" onClick={() => this._submitNotifyMe()} class="ti-button-primary hydrated">{this._localeStrings['submitLabel']}</ti-button>,
            <ti-button slot="action" appearance="secondary" onClick={() => this._closeDialog()} class="ti-button-secondary hydrated">{this._localeStrings['cancelLabel']}</ti-button>
        ]);
        return dialog;
    }

    /**
     * Opens the provided dialog
     * @param dialogContent - content to be opened
     */
    private _openDialog(dialogContent) {
        if (!this._dialogService) {
            const com = (window as any).com;
            if (com && com.TI && com.TI.FeatureDialogService) {
                this._dialogService = com.TI.FeatureDialogService;
            }
        }
        if (!this._dialogService) {
            return;
        }
        //Open the dialog
        const dialogConfig = {
            class: 'ti-notify-me-dialog',
            content: dialogContent,
            modal: true
        };
        this._dialogService.openDialog(dialogConfig);
    }

    /**
     * Prepares to open the NotifyMe dialog
     */
    private _openNotifyMeDialog() {
        const dialogContent = this._fetchDialogContent();
        this._openDialog(dialogContent);
    }

    /**
     * Submits the quantity/email request to the endpoint, if they are valid
     */
    private async _submitNotifyMe() {
        const requestBod = {
            'opn': this.opn,
            'locale': this.locale
        };
        try {
            const notifyMeFetch = await fetch(`${this.endpointUrl}`,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    method: 'POST',
                    body: JSON.stringify(requestBod)
                });
            if (!notifyMeFetch.ok) {
                const err = new Error(`Received ${notifyMeFetch.status}: ${notifyMeFetch.statusText}`);
                throw err;
            }
            else {
                const dialog = ([
                    <h2 slot="title">{this._localeStrings['confirmationLabel']}</h2>,
                    <div class="ti-notify-me-body">
                        <p innerHTML={this._localeStrings['confirmationText'].replace('{}', this.opn)}></p>
                    </div>,
                    <ti-button slot="action" appearance="primary" onClick={() => this._closeDialog()} class="ti-button-primary hydrated">{this._localeStrings['closeLabel']}</ti-button>]);
                this._openDialog(dialog);
                return;
            }
        }
        catch (err) {
            const dialog = ([
                <h2 slot="title">{this._localeStrings['notifyMeLabel']}</h2>,
                <div class="ti-notify-me-body">
                    <p>{this._localeStrings['errorMessageLabel']}</p>
                </div>,
                <ti-button slot="action" appearance="primary" onClick={() => this._closeDialog()} class="ti-button-primary hydrated">{this._localeStrings['closeLabel']}</ti-button>]);
            this._openDialog(dialog);
            return;
        }
    }

    /**
     * Renders the component
     */
    render() {
        return (
            <Host>
                 <ti-button
                    appearance="link"
                    onClick={() => {
                        this._openNotifyMeDialog();
                    }}>
                    <div class="ti-notify-me-icon-text">
                        <ti-svg-icon size="s" appearance="secondary" icon-set="actions" aria-hidden="true">alarm</ti-svg-icon>
                        <span>{this._localeStrings['notifyMeLabel']}</span>
                    </div>
                </ti-button>
            </Host>
        );
    }
}
